import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { ClientTask, ClientDocument, ClientInvoice, ClientMessage } from "@/lib/types";

export default function ClientPortalPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileDescription, setFileDescription] = useState("");
  const [messageText, setMessageText] = useState("");
  
  // Get authentication status from auth hook
  const { user, isLoading: userLoading, logoutMutation } = useAuth();
  
  // Fetch client tasks
  const { data: tasks = [], isLoading: tasksLoading } = useQuery<ClientTask[]>({
    queryKey: ['/api/client/tasks'],
    enabled: !!user
  });
  
  // Fetch client documents
  const { data: documents = [], isLoading: documentsLoading, refetch: refetchDocuments } = useQuery<ClientDocument[]>({
    queryKey: ['/api/client/documents'],
    enabled: !!user
  });
  
  // Fetch client invoices
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<ClientInvoice[]>({
    queryKey: ['/api/client/invoices'],
    enabled: !!user
  });
  
  // Fetch client messages
  const { data: messages = [], isLoading: messagesLoading, refetch: refetchMessages } = useQuery<ClientMessage[]>({
    queryKey: ['/api/client/messages'],
    enabled: !!user
  });
  
  // Upload document mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      // Send the actual FormData with the file
      return fetch('/api/client/documents/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      }).then(res => {
        if (!res.ok) {
          throw new Error('Upload failed');
        }
        return res.json();
      });
    },
    onSuccess: () => {
      toast({
        title: "Document Uploaded",
        description: "Your document has been uploaded successfully.",
      });
      setSelectedFile(null);
      setFileDescription("");
      refetchDocuments();
    },
    onError: (error) => {
      toast({
        title: "Upload Failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  // Send message mutation
  const messageMutation = useMutation({
    mutationFn: (message: string) => {
      return apiRequest("POST", "/api/client/messages", { message });
    },
    onSuccess: () => {
      toast({
        title: "Message Sent",
        description: "Your message has been sent to Sarah.",
      });
      setMessageText("");
      refetchMessages();
    },
    onError: (error) => {
      toast({
        title: "Message Failed",
        description: error.message || "Failed to send message. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  // Complete task mutation
  const completeTaskMutation = useMutation({
    mutationFn: (taskId: number) => {
      return apiRequest("PATCH", `/api/client/tasks/${taskId}/complete`, {});
    },
    onSuccess: () => {
      toast({
        title: "Task Updated",
        description: "Task has been marked as complete.",
      });
      // Refetch tasks after completion
      queryClient.invalidateQueries({ queryKey: ['/api/client/tasks'] });
    },
    onError: (error) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update task. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };
  
  // Handle file upload
  const handleUpload = () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('description', fileDescription);
    
    uploadMutation.mutate(formData);
  };
  
  // Handle message sending
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim()) {
      toast({
        title: "Empty Message",
        description: "Please enter a message before sending.",
        variant: "destructive",
      });
      return;
    }
    
    messageMutation.mutate(messageText);
  };
  
  // Handle task completion
  const handleCompleteTask = (taskId: number) => {
    completeTaskMutation.mutate(taskId);
  };
  
  // Handle logout
  const handleLogout = () => {
    logoutMutation.mutate();
  };
  
  if (userLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin mb-4 h-12 w-12 border-4 border-[#7d9b76] border-t-transparent rounded-full mx-auto"></div>
          <p className="text-[#1a365d] font-medium">Loading client portal...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="bg-[#1a365d] text-white">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center">
            <Link href="/" className="text-xl font-serif font-bold">
              SW <span className="text-[#7d9b76]">Accounting</span>
            </Link>
            <div className="ml-8 hidden md:block">Client Portal</div>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <>
                <div className="hidden md:flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[#7d9b76] text-white">
                      {user.username.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span>{user.username}</span>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-[#7d9b76] hover:bg-transparent"
                >
                  <i className="fas fa-sign-out-alt mr-2"></i>
                  <span className="hidden md:inline">Logout</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-serif text-3xl font-bold text-[#1a365d]">Welcome to Your Client Portal</h1>
          <p className="text-[#343a40]">Manage your tasks, documents, and communication with SW Accounting.</p>
        </div>
        
        <Tabs defaultValue="tasks" className="w-full">
          <TabsList className="grid grid-cols-5 mb-8">
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>
          
          <TabsContent value="tasks" className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-4">Your Tasks</h2>
            
            {tasksLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-[#7d9b76] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading your tasks...</p>
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-8 bg-[#f8f9fa] rounded-lg">
                <i className="fas fa-check-circle text-[#7d9b76] text-4xl mb-4"></i>
                <p className="text-lg font-medium">You have no pending tasks</p>
                <p className="text-gray-500">Any assigned tasks will appear here.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {tasks.map((task) => (
                  <Card key={task.id} className={`border-l-4 ${task.completed ? 'border-l-green-500' : 'border-l-[#7d9b76]'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`text-sm font-medium px-2 py-1 rounded ${
                              task.priority === 'high' ? 'bg-red-100 text-red-800' : 
                              task.priority === 'medium' ? 'bg-orange-100 text-orange-800' : 
                              'bg-green-100 text-green-800'
                            }`}>
                              {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Due: {formatDate(new Date(task.dueDate))}
                            </div>
                          </div>
                          <h3 className="font-bold text-[#1a365d] mb-1">{task.title}</h3>
                          <p className="text-gray-600">{task.description}</p>
                          
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">Progress</span>
                              <span className="text-sm font-medium">{task.progress}%</span>
                            </div>
                            <Progress value={task.progress} className="h-2" />
                          </div>
                        </div>
                        
                        <div className="ml-4">
                          {!task.completed && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-[#7d9b76] text-[#7d9b76] hover:bg-[#7d9b76] hover:text-white"
                              onClick={() => handleCompleteTask(task.id)}
                            >
                              Mark Complete
                            </Button>
                          )}
                          {task.completed && (
                            <div className="flex items-center text-green-500">
                              <i className="fas fa-check-circle mr-2"></i>
                              <span>Completed</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="documents" className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
              <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-4 md:mb-0">Your Documents</h2>
              
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-[#7d9b76] hover:bg-opacity-90">
                    <i className="fas fa-upload mr-2"></i>
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                    <DialogDescription>
                      Upload a document to share with SW Accounting.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="grid w-full max-w-sm items-center gap-1.5">
                      <Label htmlFor="file">File</Label>
                      <Input 
                        type="file" 
                        id="file" 
                        onChange={handleFileChange}
                        className="cursor-pointer"
                      />
                    </div>
                    
                    <div className="grid w-full gap-1.5">
                      <Label htmlFor="description">Description</Label>
                      <Input 
                        type="text" 
                        id="description" 
                        value={fileDescription}
                        onChange={(e) => setFileDescription(e.target.value)}
                        placeholder="Enter a description for this file"
                      />
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button 
                      className="bg-[#7d9b76] hover:bg-opacity-90"
                      onClick={handleUpload}
                      disabled={uploadMutation.isPending || !selectedFile}
                    >
                      {uploadMutation.isPending ? "Uploading..." : "Upload"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            
            {documentsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-[#7d9b76] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading your documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 bg-[#f8f9fa] rounded-lg">
                <i className="fas fa-file-alt text-[#7d9b76] text-4xl mb-4"></i>
                <p className="text-lg font-medium">No documents found</p>
                <p className="text-gray-500">Upload a document to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Date Uploaded</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((document) => (
                      <TableRow key={document.id}>
                        <TableCell className="font-medium">{document.name}</TableCell>
                        <TableCell>{document.description}</TableCell>
                        <TableCell>{formatDate(new Date(document.uploadedAt))}</TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <i className={`fas fa-${
                              document.type === 'pdf' ? 'file-pdf' : 
                              document.type === 'doc' ? 'file-word' : 
                              document.type === 'xls' ? 'file-excel' : 
                              'file'
                            } text-[#1a365d] mr-2`}></i>
                            <span>{document.type.toUpperCase()}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <a 
                            href={document.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium hover:bg-[#f8f9fa] h-9 px-4 py-2"
                          >
                            <i className="fas fa-download mr-2"></i>
                            Download
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="invoices" className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-6">Your Invoices</h2>
            
            {invoicesLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-[#7d9b76] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading your invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8 bg-[#f8f9fa] rounded-lg">
                <i className="fas fa-file-invoice-dollar text-[#7d9b76] text-4xl mb-4"></i>
                <p className="text-lg font-medium">No invoices yet</p>
                <p className="text-gray-500">Your invoices will appear here once they are generated.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                        <TableCell>{formatDate(new Date(invoice.issueDate))}</TableCell>
                        <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 
                            invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-red-100 text-red-800'
                          }`}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(new Date(invoice.dueDate))}</TableCell>
                        <TableCell className="text-right">
                          <a 
                            href={invoice.pdfUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium hover:bg-[#f8f9fa] h-9 px-4 py-2"
                          >
                            <i className="fas fa-download mr-2"></i>
                            Download
                          </a>
                          {invoice.status !== 'paid' && (
                            <a 
                              href={invoice.paymentUrl} 
                              className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-[#7d9b76] text-white hover:bg-opacity-90 h-9 px-4 py-2 ml-2"
                            >
                              <i className="fas fa-credit-card mr-2"></i>
                              Pay
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="messages" className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-6">Messages</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <Card className="mb-4">
                  <CardHeader className="pb-3">
                    <CardTitle>Message History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {messagesLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin h-8 w-8 border-4 border-[#7d9b76] border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p>Loading message history...</p>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center py-8 bg-[#f8f9fa] rounded-lg">
                        <i className="fas fa-comments text-[#7d9b76] text-4xl mb-4"></i>
                        <p className="text-lg font-medium">No messages yet</p>
                        <p className="text-gray-500">Start a conversation with Sarah.</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          {messages.map((message) => (
                            <div 
                              key={message.id} 
                              className={`flex ${message.fromClient ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[80%] p-3 rounded-lg ${
                                message.fromClient ? 
                                'bg-[#1a365d] text-white' : 
                                'bg-[#f8f9fa]'
                              }`}>
                                <div className="flex items-center mb-1">
                                  <span className="font-medium">
                                    {message.fromClient ? 'You' : 'Sarah Willardsen'}
                                  </span>
                                  <span className="text-xs opacity-70 ml-2">
                                    {new Date(message.timestamp).toLocaleString()}
                                  </span>
                                </div>
                                <p>{message.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              <div>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>Send a Message</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSendMessage}>
                      <div className="space-y-4">
                        <div className="grid gap-2">
                          <Label htmlFor="message">Message</Label>
                          <Textarea 
                            id="message"
                            placeholder="Type your message here..."
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            className="min-h-[150px]"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-[#7d9b76] hover:bg-opacity-90"
                          disabled={messageMutation.isPending}
                        >
                          {messageMutation.isPending ? "Sending..." : "Send Message"}
                        </Button>
                      </div>
                    </form>
                    
                    <div className="mt-6">
                      <h3 className="font-medium mb-2">Contact Information</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <i className="fas fa-envelope text-[#7d9b76] mr-2"></i>
                          <a href="mailto:sarah@swaccountingpro.com" className="hover:underline">
                            sarah@swaccountingpro.com
                          </a>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-phone text-[#7d9b76] mr-2"></i>
                          <a href="tel:8017916900" className="hover:underline">
                            801-791-6900
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="profile" className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-6">Your Profile</h2>
            
            {user ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex flex-col items-center">
                        <Avatar className="h-24 w-24 mb-4">
                          <AvatarFallback className="bg-[#7d9b76] text-white text-2xl">
                            {user.username.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <h3 className="text-xl font-medium">{user.username}</h3>
                        <p className="text-gray-500">Client since {user.createdAt ? formatDate(new Date(user.createdAt)) : 'N/A'}</p>
                        
                        <div className="mt-6 w-full">
                          <Button className="w-full bg-[#1a365d] hover:bg-opacity-90" onClick={handleLogout}>
                            <i className="fas fa-sign-out-alt mr-2"></i>
                            Logout
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="md:col-span-2">
                  <Card className="mb-6">
                    <CardHeader className="pb-3">
                      <CardTitle>Account Security</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Alert className="mb-4">
                        <i className="fas fa-shield-alt text-[#7d9b76] mr-2"></i>
                        <AlertTitle>Security Recommendation</AlertTitle>
                        <AlertDescription>
                          We recommend changing your password periodically to keep your account secure.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="space-y-4">
                        <div className="grid w-full max-w-sm items-center gap-1.5">
                          <Label htmlFor="current-password">Current Password</Label>
                          <Input type="password" id="current-password" />
                        </div>
                        
                        <div className="grid w-full max-w-sm items-center gap-1.5">
                          <Label htmlFor="new-password">New Password</Label>
                          <Input type="password" id="new-password" />
                        </div>
                        
                        <div className="grid w-full max-w-sm items-center gap-1.5">
                          <Label htmlFor="confirm-password">Confirm New Password</Label>
                          <Input type="password" id="confirm-password" />
                        </div>
                        
                        <Button className="bg-[#7d9b76] hover:bg-opacity-90">
                          Update Password
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle>Notification Preferences</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-base">Email Notifications</Label>
                            <p className="text-sm text-gray-500">Receive notifications about new tasks and documents via email</p>
                          </div>
                          <div>
                            <Switch defaultChecked id="email-notifications" />
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-base">Invoice Alerts</Label>
                            <p className="text-sm text-gray-500">Get alerts when a new invoice is available or due soon</p>
                          </div>
                          <div>
                            <Switch defaultChecked id="invoice-alerts" />
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-base">Message Notifications</Label>
                            <p className="text-sm text-gray-500">Receive notifications when you get a new message</p>
                          </div>
                          <div>
                            <Switch defaultChecked id="message-notifications" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-[#7d9b76] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading profile information...</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
